/**
 * useDetailSidebarResize - generic wrapper around shared useSidebarResize
 * for right-side detail sidebars (ADR detail, Resource detail, etc.).
 */

import { useCallback } from 'react';
import { useSidebarResize } from '../../lib/useSidebarResize';
import type { CanvasUiStore } from '../stores/canvasUiStore';

interface DetailSidebarConfig {
  storageKeyWidth: string;
  storageKeyCollapsed: string;
  /** Store event type, e.g. 'adrDetail.update' or 'resourceDetail.update' */
  eventType: string;
  /** Property name for width in the store, e.g. 'adrDetailWidth' */
  widthProp: string;
  /** Property name for collapsed in the store, e.g. 'isAdrDetailCollapsed' */
  collapsedProp: string;
  /** Property name for resizing in the store, e.g. 'isAdrDetailResizing' */
  resizingProp: string;
}

const DETAIL_MIN = 280;
const DETAIL_MAX = 900;
const DETAIL_DEFAULT = 380;
const DETAIL_COLLAPSED_WIDTH = 0;
const DETAIL_COLLAPSE_THRESHOLD = 120;

export function useDetailSidebarResize(uiStore: CanvasUiStore, config: DetailSidebarConfig) {
  const onSidebarUpdate = useCallback((patch: { sidebarWidth?: number; isCollapsed?: boolean; isSidebarResizing?: boolean }) => {
    const mapped: Record<string, unknown> = { type: config.eventType };
    if (patch.sidebarWidth !== undefined) mapped[config.widthProp] = patch.sidebarWidth;
    if (patch.isCollapsed !== undefined) mapped[config.collapsedProp] = patch.isCollapsed;
    if (patch.isSidebarResizing !== undefined) mapped[config.resizingProp] = patch.isSidebarResizing;
    uiStore.send(mapped as Parameters<typeof uiStore.send>[0]);
  }, [uiStore, config]);

  return useSidebarResize({
    storageKeyWidth: config.storageKeyWidth,
    storageKeyCollapsed: config.storageKeyCollapsed,
    min: DETAIL_MIN,
    max: DETAIL_MAX,
    defaultWidth: DETAIL_DEFAULT,
    collapsedWidth: DETAIL_COLLAPSED_WIDTH,
    collapseThreshold: DETAIL_COLLAPSE_THRESHOLD,
    oversteer: true,
    side: 'right',
    onSidebarUpdate,
  });
}

// Pre-configured for ADR detail sidebar
const ADR_CONFIG: DetailSidebarConfig = {
  storageKeyWidth: 'roadmap_2026_canvas_adr_detail_width',
  storageKeyCollapsed: 'roadmap_2026_canvas_adr_detail_collapsed',
  eventType: 'adrDetail.update',
  widthProp: 'adrDetailWidth',
  collapsedProp: 'isAdrDetailCollapsed',
  resizingProp: 'isAdrDetailResizing',
};

export function useAdrDetailSidebarResize(uiStore: CanvasUiStore) {
  return useDetailSidebarResize(uiStore, ADR_CONFIG);
}

// Pre-configured for Resource detail sidebar
const RESOURCE_CONFIG: DetailSidebarConfig = {
  storageKeyWidth: 'roadmap_2026_canvas_resource_detail_width',
  storageKeyCollapsed: 'roadmap_2026_canvas_resource_detail_collapsed',
  eventType: 'resourceDetail.update',
  widthProp: 'resourceDetailWidth',
  collapsedProp: 'isResourceDetailCollapsed',
  resizingProp: 'isResourceDetailResizing',
};

export function useResourceDetailSidebarResize(uiStore: CanvasUiStore) {
  return useDetailSidebarResize(uiStore, RESOURCE_CONFIG);
}
